import { NumberDecimalFormat } from '@components/shared/Format'
import { Constants } from '@models/Constants'
import { Country } from '@models/Country'
import { Store } from '@stores/Store'
import { LeadershipPlusFqGrowthValue, LeadershipPlusLevelAchievedValue } from '@utils/sections/LeadershipPlusIncentive.util'

export class LeadershipPlusIncentiveCalculation {
  store!: Store

  constructor(store: Store) {
    this.store = store
  }

  // Leadership Plus Incentive

  get leadershipPlusIncentiveAnnualMultipier(): number {
    const { store } = this
    const { countryData, leadershipPlusIncentiveStore } = store
    const { leadershipPlusIncentiveFqGrowth } = leadershipPlusIncentiveStore

    if (!this.leadershipPlusIncentiveAllCriteriaMet) { return 0 }

    /* eslint-disable indent */
    switch (leadershipPlusIncentiveFqGrowth) {
      case LeadershipPlusFqGrowthValue.MAINTAIN: { return countryData.frontlineGrowthIncentive.multiplierMaintain }
      case LeadershipPlusFqGrowthValue.FQS_1_TO_5: { return countryData.frontlineGrowthIncentive.multiplier1to5 }
      case LeadershipPlusFqGrowthValue.FQS_6_PLUS: { return countryData.frontlineGrowthIncentive.multiplier6plus }
      default: { return 0 }
    }
  }

  get leadershipPlusIncentive(): number {
    const { store } = this
    const { leadershipPlusIncentiveStore } = store
    const { leadershipPlusIncentiveActive } = leadershipPlusIncentiveStore

    if (!leadershipPlusIncentiveActive) { return 0 }

    return this.leadershipPlusIncentiveAnnualIncentiveAmount
  }

  get leadershipPlusIncentiveAnnualIncentiveAmount(): number {
    const annualSumAmountWithMultiplier = this.leadershipPlusIncentiveAnnualSumOfLbFlbDepth * this.leadershipPlusIncentiveAnnualMultipier
    if (this.leadershipPlusIncentiveAllCriteriaMet && annualSumAmountWithMultiplier > 0) {
      return Math.min(NumberDecimalFormat(annualSumAmountWithMultiplier), NumberDecimalFormat(this.leadershipPlusIncentivePaymentCap))
    }

    return 0
  }

  get leadershipPlusIncentiveAnnualPPVCriteria(): boolean {
    const { store } = this
    const { personalAnnualPV, globalStore, leadershipPlusIncentiveStore } = store
    const { country } = globalStore
    const { leadershipPlusIncentiveActive } = leadershipPlusIncentiveStore

    if (!leadershipPlusIncentiveActive) { return false }
    if (
      (personalAnnualPV >= Constants.ANNUAL_PPV_EUROPE && (country !== Country.AU && country !== Country.NZ)) ||
      (personalAnnualPV >= Constants.ANNUAL_PPV_ANZ && (country === Country.AU || country === Country.NZ))
    ) {
      return true
    }

    return false
  }

  get leadershipPlusIncentiveAnnualSumOfLbFlbDepth(): number {
    return this.leadershipPlusIncentiveMonthlySumOfLbFlbDepth * 12
  }

  get leadershipPlusIncentiveMonthlySumOfLbFlbDepth(): number {
    const { store } = this
    const { leadershipBonus, fosterBonus, depthCalculation } = store
    const { depthBonus } = depthCalculation

    return NumberDecimalFormat(leadershipBonus + fosterBonus + depthBonus)
  }

  get leadershipPlusIncentiveAllCriteriaMet(): boolean {
    return this.leadershipPlusIncentiveLevelEligibility && this.leadershipPlusIncentiveAnnualPPVCriteria && this.leadershipPlusIncentiveRubyPvCriteria
  }

  get leadershipPlusIncentiveRubyPvCriteria(): boolean {
    const { store } = this
    const { rubyPV } = store

    return (rubyPV * 12) >= this.leadershipPlusIncentiveAnnualRubyPVRequirement
  }

  get leadershipPlusIncentiveLevelEligibility(): boolean {
    const { store } = this
    const { leadershipPlusIncentiveStore } = store
    const { leadershipPlusIncentiveLevelAchieved } = leadershipPlusIncentiveStore

    return leadershipPlusIncentiveLevelAchieved !== LeadershipPlusLevelAchievedValue.BELOW_F_PLATINUM
  }

  get leadershipPlusIncentiveAnnualPPVRequirement(): number {
    const { store } = this
    const { countryData } = store

    return countryData.frontlineGrowthIncentiveAnnualVolume!
  }

  get leadershipPlusIncentiveAnnualRubyPVRequirement(): number {
    const { store } = this
    const { countryData, leadershipPlusIncentiveStore } = store
    const { leadershipPlusIncentiveLevelAchieved } = leadershipPlusIncentiveStore

    if (leadershipPlusIncentiveLevelAchieved !== LeadershipPlusLevelAchievedValue.DIAMOND_AND_ABOVE) {
      return countryData.frontlineGrowthIncentiveRubyPVRequirement!
    }

    return 0
  }

  get leadershipPlusIncentivePaymentCap(): number {
    const { store } = this
    const { countryData } = store

    return countryData.frontlineGrowthIncentiveAmountCap!
  }

}
